import Choices from 'choices.js/assets/scripts/dist/choices.min.js';

export type ChoiceRecord = { id: number | string } & Record<string, any>;

type PageResponse = { success: boolean; data: ChoiceRecord[]; has_more: boolean };

export interface InfiniteChoicesOptions {
  el: HTMLSelectElement;
  url: string;
  placeholder: string;
  searchPlaceholder: string;
  buildLabel: (record: ChoiceRecord) => string;
  extraParams?: () => Record<string, string | number>;
  excludeIds?: () => string[];
  onError?: (message: string) => void;
  onChange?: () => void;
  // Extra Choices.js config merged over the defaults (e.g. { position: 'bottom' }).
  choicesConfig?: Record<string, any>;
  // Fired when the dropdown opens/closes — e.g. to overlay-position it in a modal.
  onShowDropdown?: () => void;
  onHideDropdown?: () => void;
  autoLoad?: boolean;
}

const SCROLL_THRESHOLD_PX = 48;
const SEARCH_DEBOUNCE_MS = 300;

const BASE_CONFIG = {
  searchEnabled: true,
  searchChoices: false, // server-side search — do not filter loaded choices client-side
  shouldSort: false,
  itemSelectText: '',
  placeholder: true,
};

export class InfiniteChoices {
  private opts: InfiniteChoicesOptions;
  private choices: any = null;
  private records = new Map<string, ChoiceRecord>();

  private page = 0;
  private query = '';
  private hasMore = true;
  private loading = false;
  private seq = 0;

  private pendingSelection: ChoiceRecord | null = null;
  private preselectedId: string | null = null;

  private listEl: HTMLElement | null = null;
  private searchTimer: number | null = null;

  private readonly onScroll = () => this.handleScroll();
  private readonly onSearchInput = () => this.handleSearchInput();
  private readonly onShowDropdown = () => { this.opts.onShowDropdown?.(); this.fillViewportIfNeeded(); };
  private readonly onHideDropdown = () => { this.opts.onHideDropdown?.(); };

  constructor(opts: InfiniteChoicesOptions) {
    this.opts = opts;
  }

  init(): void {
    this.choices = new Choices(this.opts.el, {
      ...BASE_CONFIG,
      ...(this.opts.choicesConfig ?? {}),
      placeholderValue: this.opts.placeholder,
      searchPlaceholderValue: this.opts.searchPlaceholder,
    });

    if (this.opts.onChange) {
      this.opts.el.addEventListener('change', this.opts.onChange);
    }
    this.opts.el.addEventListener('showDropdown', this.onShowDropdown);
    this.opts.el.addEventListener('hideDropdown', this.onHideDropdown);

    const input = this.searchInput();
    if (input) input.addEventListener('input', this.onSearchInput);

    this.listEl = this.dropdownListEl();
    if (this.listEl) this.listEl.addEventListener('scroll', this.onScroll);

    if (this.opts.autoLoad !== false) this.reload();
  }

  // Reload from page 1, preserving the current search query and extra params.
  reload(): void {
    this.page = 0;
    this.hasMore = true;
    this.fetchPage(true);
  }

  reset(): void {
    this.query = '';
    this.pendingSelection = null;
    this.preselectedId = null;
    const input = this.searchInput();
    if (input) input.value = '';
    this.reload();
  }

  selectRecord(record: ChoiceRecord): void {
    this.records.set(String(record.id), record);
    this.pendingSelection = record;
    this.preselectedId = String(record.id);
    this.reload();
  }

  getSelectedId(): string {
    const selected = this.choices && this.choices.getValue();
    return selected && selected.value ? String(selected.value) : '';
  }

  getSelected(): ChoiceRecord | null {
    const id = this.getSelectedId();
    return id ? (this.records.get(id) ?? null) : null;
  }

  isStale(): boolean {
    const passed = this.choices && this.choices.passedElement;
    const el = passed && (passed instanceof Element ? passed : passed.element);
    return !el || !document.body.contains(el);
  }

  destroy(): void {
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    if (this.listEl) this.listEl.removeEventListener('scroll', this.onScroll);
    this.opts.el.removeEventListener('showDropdown', this.onShowDropdown);
    this.opts.el.removeEventListener('hideDropdown', this.onHideDropdown);
    if (this.opts.onChange) this.opts.el.removeEventListener('change', this.opts.onChange);
    const input = this.searchInput();
    if (input) input.removeEventListener('input', this.onSearchInput);
    try { this.choices && this.choices.destroy(); } catch (_e) { /* already detached */ }
    this.choices = null;
    this.records.clear();
    this.listEl = null;
  }

  private searchInput(): HTMLInputElement | null {
    const input = this.choices && this.choices.input;
    return input && typeof input.addEventListener === 'function' ? (input as HTMLInputElement) : null;
  }

  private dropdownListEl(): HTMLElement | null {
    const wrapper = this.opts.el.closest('.choices') as HTMLElement | null;
    return wrapper?.querySelector<HTMLElement>('.choices__list--dropdown .choices__list') ?? null;
  }

  private placeholderChoice() {
    return { value: '', label: this.opts.placeholder, placeholder: true, selected: true };
  }

  private toChoiceItems(records: ChoiceRecord[]): { value: string; label: string }[] {
    const excluded = new Set((this.opts.excludeIds?.() ?? []).map(String));
    if (this.preselectedId) excluded.add(this.preselectedId);
    return records
      .filter(record => !excluded.has(String(record.id)))
      .map(record => ({ value: String(record.id), label: this.opts.buildLabel(record) }));
  }

  private handleSearchInput(): void {
    const input = this.searchInput();
    const value = (input?.value ?? '').trim();
    if (value === this.query) return;
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => {
      this.query = value;
      this.reload();
    }, SEARCH_DEBOUNCE_MS);
  }

  private handleScroll(): void {
    if (!this.listEl || this.loading || !this.hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = this.listEl;
    if (scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD_PX) {
      this.fetchPage(false);
    }
  }

  private fillViewportIfNeeded(): void {
    if (!this.listEl || this.loading || !this.hasMore) return;
    if (this.listEl.clientHeight > 0 && this.listEl.scrollHeight <= this.listEl.clientHeight + 4) {
      this.fetchPage(false);
    }
  }

  private fetchPage(reset: boolean): void {
    if (this.loading) return;
    if (!reset && !this.hasMore) return;

    this.loading = true;
    const requestSeq = ++this.seq;
    const nextPage = reset ? 1 : this.page + 1;

    const params: Record<string, any> = { page: nextPage, ...(this.opts.extraParams?.() ?? {}) };
    if (this.query) params.q = this.query;

    $.ajax({
      url: this.opts.url,
      method: 'GET',
      data: params,
      success: (response: PageResponse) => {
        if (requestSeq !== this.seq) return; // a newer request superseded this one
        if (!response || !response.success) {
          this.loading = false;
          return;
        }
        response.data.forEach(record => this.records.set(String(record.id), record));
        const items = this.toChoiceItems(response.data);
        if (reset) {
          const sel = this.pendingSelection;
          const list = sel
            ? [
                { value: '', label: this.opts.placeholder, placeholder: true },
                { value: String(sel.id), label: this.opts.buildLabel(sel), selected: true },
                ...items,
              ]
            : [this.placeholderChoice(), ...items];
          this.choices.setChoices(list, 'value', 'label', true);
          this.pendingSelection = null;
        } else {
          this.choices.setChoices(items, 'value', 'label', false);
        }
        this.page = nextPage;
        this.hasMore = !!response.has_more && response.data.length > 0;
        this.loading = false;
        this.fillViewportIfNeeded();
      },
      error: () => {
        if (requestSeq !== this.seq) return;
        this.loading = false;
        this.opts.onError?.('Failed to load options.');
      },
    });
  }
}
